<template>
  <aside
    class="customer-sidebar"
    :style="{
      background:
        theme.cardBodyBg,
      color:
        theme.cardBodyColor
    }"
  >
    <!-- ============================================================
         BRAND
    ============================================================= -->
    <div
      class="sidebar-brand"
      :style="{
        background:
          theme.cardTitleBgColor,
        color:
          theme.cardTitleTextColor
      }"
    >
      <v-btn
        icon
        variant="text"
        size="small"
        class="sidebar-brand-menu"
        :style="{
          ...sysTypo.sectionTypography.button,
          color:
            theme.cardTitleTextColor
        }"
        @click="emit('close')"
      >
        <v-icon>
          mdi-menu
        </v-icon>
      </v-btn>

      <router-link
        :to="{
          name: 'PublicHome'
        }"
        class="sidebar-logo-link"
      >
        <img
          src="@/assets/newnbblacklogo.png"
          alt="Nexgbits"
          class="sidebar-logo"
        />
      </router-link>
    </div>

    <!-- ============================================================
         MENU
    ============================================================= -->
    <nav class="sidebar-navigation">
      <button
        v-for="item in visiblePrimaryMenu"
        :key="item.name"
        type="button"
        class="sidebar-menu-item"
        :class="{
          active:
            isActive(item)
        }"
        :style="
          sysTypo.sectionTypography.body
        "
        @click="navigate(item)"
      >
        <v-icon
          size="20"
          class="sidebar-menu-icon"
        >
          {{ item.icon }}
        </v-icon>

        <span class="sidebar-menu-label">
          {{ item.title }}
        </span>

        <span
          v-if="item.badge"
          class="sidebar-menu-badge"
          :style="
            sysTypo.sectionTypography.caption
          "
        >
          {{ item.badge }}
        </span>
      </button>

      <div class="sidebar-divider" />

      <button
        v-for="item in visibleAccountMenu"
        :key="item.name"
        type="button"
        class="sidebar-menu-item"
        :class="{
          active:
            isActive(item)
        }"
        :style="
          sysTypo.sectionTypography.body
        "
        @click="navigate(item)"
      >
        <v-icon
          size="20"
          class="sidebar-menu-icon"
        >
          {{ item.icon }}
        </v-icon>

        <span class="sidebar-menu-label">
          {{ item.title }}
        </span>
      </button>
    </nav>

    <!-- ============================================================
         MEMBERSHIP CTA
    ============================================================= -->
    <div class="sidebar-bottom">
      <v-card
        rounded="xl"
        elevation="0"
        class="membership-upgrade-card"
        :style="{
          background:
            theme.cardAlternateBgColor ||
            theme.cardBodyBg,
          color:
            theme.cardBodyColor
        }"
      >
        <v-card-text class="text-center pa-5">
          <v-avatar
            size="58"
            class="membership-upgrade-icon"
          >
            <v-icon
              color="orange-darken-1"
              size="30"
            >
              mdi-crown
            </v-icon>
          </v-avatar>

          <div
            class="text-subtitle-1 font-weight-bold mt-4"
            :style="
              sysTypo.sectionTypography.h3
            "
          >
            Upgrade Your Membership
          </div>

          <div
            class="text-caption text-medium-emphasis mt-2"
            :style="
              sysTypo.sectionTypography.caption
            "
          >
            Unlock more benefits
            and rewards.
          </div>

          <v-btn
            block
            variant="flat"
            rounded="lg"
            class="text-none mt-4"
            :style="{
              ...sysTypo.sectionTypography.button,
              background:
                theme.btnBgColor,
              color:
                theme.btnColor
            }"
            @click="goToMembership"
          >
            View Plans

            <v-icon
              end
              size="18"
            >
              mdi-arrow-right
            </v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
  </aside>
</template>

<script setup>
import {
  computed
} from "vue"

import {
  useRoute,
  useRouter
} from "vue-router"

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth"

import {
  useSysTypo
} from "@/store/useSysTypo"

const emit =
  defineEmits([
    "close"
  ])

const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const sysTypo =
  useSysTypo()

const route =
  useRoute()

const router =
  useRouter()

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.sectionTheme ||
      {}
  )

// ============================================================
// MENU
// ============================================================

const primaryMenu =
  computed(() => [
    {
      name:
        "CustomerDashboard",

      title:
        "Overview",

      icon:
        "mdi-shopping-outline"
    },

    {
      name:
        "CustomerOrders",

      title:
        "My Orders",

      icon:
        "mdi-cart-outline"
    },

    {
      name:
        "CustomerInvoices",

      title:
        "My Invoices",

      icon:
        "mdi-file-document-outline"
    },

    {
      name:
        "CustomerPaymentHistory",

      title:
        "Payment History",

      icon:
        "mdi-credit-card-outline"
    },

    {
      name:
        "CustomerMembership",

      title:
        "Membership",

      icon:
        "mdi-medal-outline"
    },

    {
      name:
        "CustomerAppointments",

      title:
        "Appointments",

      icon:
        "mdi-calendar-outline"
    },

    {
      name:
        "CustomerInsurance",

      title:
        "Insurance",

      icon:
        "mdi-shield-check-outline"
    },

    {
      name:
        "CustomerAddresses",

      title:
        "Addresses",

      icon:
        "mdi-map-marker-outline"
    },

    {
      name:
        "CustomerMessages",

      title:
        "Messages",

      icon:
        "mdi-email-outline",

      badge:
        null
    }
  ])

const accountMenu =
  computed(() => [
    {
      name:
        "CustomerProfile",

      title:
        "Profile",

      icon:
        "mdi-account-circle-outline"
    },

    {
      name:
        "CustomerSettings",

      title:
        "Settings",

      icon:
        "mdi-cog-outline"
    }
  ])

// ============================================================
// PERMISSION FILTERING
// ============================================================

function canAccessItem(
  item
) {
  if (
    !item
  ) {
    return false
  }

  if (
    item.public === true
  ) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(
      item.permission
    )
  ) {
    return false
  }

  return true
}

function filterByAccess(
  items
) {
  return (
    items || []
  )
    .map(
      item => {
        if (
          !canAccessItem(
            item
          )
        ) {
          return null
        }

        if (
          item.children?.length
        ) {
          const children =
            filterByAccess(
              item.children
            )

          if (
            children.length === 0
          ) {
            return item.to
              ? {
                  ...item,
                  children:
                    undefined
                }
              : null
          }

          return {
            ...item,
            children
          }
        }

        return item
      }
    )
    .filter(Boolean)
}

const visiblePrimaryMenu =
  computed(() =>
    filterByAccess(
      primaryMenu.value
    )
  )

const visibleAccountMenu =
  computed(() =>
    filterByAccess(
      accountMenu.value
    )
  )

// ============================================================
// ACTIVE
// ============================================================

function isActive(
  item
) {
  return (
    route.name ===
    item.name
  )
}

// ============================================================
// NAVIGATION
// ============================================================

async function navigate(
  item
) {
  await router.push({
    name:
      item.name
  })

  emit(
    "close"
  )
}

async function goToMembership() {
  await router.push({
    name:
      "CustomerMembership"
  })

  emit(
    "close"
  )
}
</script>

<style scoped>
.customer-sidebar {
  position:
    relative;

  height:
    100%;

  min-height:
    100vh;

  display:
    flex;

  flex-direction:
    column;
}

.sidebar-brand {
  height:
    74px;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  padding:
    0 22px;

  border-bottom:
    1px solid
    rgba(
      15,
      23,
      42,
      0.06
    );
}

.sidebar-brand-menu {
  margin-left:
    -8px;
}

.sidebar-logo-link {
  display:
    flex;

  align-items:
    center;

  text-decoration:
    none;
}

.sidebar-logo {
  display:
    block;

  width:
    143px;

  height:
    auto;

  object-fit:
    contain;
}

.sidebar-navigation {
  padding:
    14px;
}

.sidebar-menu-item {
  width:
    100%;

  min-height:
    44px;

  display:
    flex;

  align-items:
    center;

  position:
    relative;

  border:
    none;

  border-radius:
    10px;

  background:
    transparent;

  color:
    inherit;

  padding:
    0 14px;

  margin-bottom:
    4px;

  cursor:
    pointer;

  text-align:
    left;

  transition:
    all 0.15s ease;
}

.sidebar-menu-item:hover {
  opacity:
    0.85;
}

.sidebar-menu-item.active {
  color:
    var(
      --v-theme-primary
    );

  font-weight:
    700;
}

.sidebar-menu-icon {
  width:
    26px;

  margin-right:
    7px;

  color:
    inherit;
}

.sidebar-menu-label {
  flex:
    1;
}

.sidebar-menu-badge {
  min-width:
    20px;

  height:
    20px;

  padding:
    0 6px;

  border-radius:
    10px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  background:
    #4138ee;

  color:
    #ffffff;

  font-weight:
    700;
}

.sidebar-divider {
  height:
    1px;

  margin:
    14px 8px;

  background:
    rgba(
      15,
      23,
      42,
      0.08
    );
}

.sidebar-bottom {
  margin-top:
    auto;

  padding:
    16px 22px 26px;
}

.membership-upgrade-icon {
  background:
    #fff0bf;
}

@media (
  max-width: 1279px
) {
  .sidebar-brand-menu {
    display:
      inline-flex;
  }
}

@media (
  min-width: 1280px
) {
  .sidebar-brand-menu {
    display:
      none;
  }
}
</style>
